<template>
  <div>
    <v-navigation-drawer
      v-if="this.configuration != null"
      width="420px"
      fixed
      right
      temporary
      v-model="drawer"
      class="bg-white h-100 d-grid-nav"
    >
      <v-card class="elevation-0">
        <v-card-title :style="backgroundStarStyle">
          <v-icon size="35" class="black--text">show_chart</v-icon>
          <span class="headline black--text">Plot Configuration</span>
          <v-spacer></v-spacer>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Simulation Plot Configuration"
          >
            The comparison of simulation results can be done on the same
            graphics. When selecting an ephemeride, a curve will be displayed
            for the two missions.
            <br />
            <br />
            <h5>Customization</h5>
            <b>Display mode </b>: results can be displayed as lines, markers
            (not connected points) with or without text attached.<br />
            <b>Lines shape </b>:
            <ul style="margin: 0">
              <li>linear : straight line between points</li>
              <li>spline : interpolation for smooth rendering</li>
            </ul>
            <b>Axes </b>: name and unit <br />
            <b>Time filter </b>: only display a part of the whole simulation
            (timeline in days). <br />
            <br />
          </exo-help-dialog>
        </v-card-title>
        <v-card-text class="p-0 pt-2">
          <v-form>
            <v-col class="px-3">
              <v-text-field
                horizontal
                label="Title"
                v-model="configuration.name"
                maxlength="100"
              >
              </v-text-field>
            </v-col>
            <v-col class="px-3">
              <v-select
                v-if="isLineChart"
                :items="scatterModesLineChart"
                item-value="value"
                item-text="label"
                label="Display mode"
                v-model="configuration.scatterMode"
                :menu-props="{ transition: 'scale-transition' }"
              >
                <template slot="selection" slot-scope="{ item }">
                  {{ item.label }}
                </template>
                <template slot="item" slot-scope="{ item }">
                  {{ item.label }}
                </template>
              </v-select>
              <v-select
                v-if="displayScatterShape"
                :items="scatterShapes"
                label="Line shape"
                item-value="value"
                item-text="label"
                v-model="configuration.scatterShape"
                :menu-props="{ transition: 'scale-transition' }"
              >
                <template slot="selection" slot-scope="{ item }">
                  {{ item.label }}
                </template>
                <template slot="item" slot-scope="{ item }">
                  {{ item.label }}
                </template>
              </v-select>
              <exo-select-field
                v-if="displayScatterText"
                :field="configuration.scatterText"
                :root="_self"
              />
            </v-col>
            <v-divider class="mt-0"></v-divider>
            <v-col class="px-3">
              <exo-path-select-row
                :field="configuration.y"
                fieldName="y"
                :root="_self"
                :style="isLineChart ? 'margin-bottom: -14px;' : ''"
              />

              <exo-path-select-row
                :disableTree="true"
                fieldName="x"
                :field="configuration.x"
                :root="_self"
              />
            </v-col>
            <v-col class="px-3">
              <v-range-slider
                v-if="
                  isDuration &&
                  Array.isArray(configuration.durationSliderValue) &&
                  configuration.durationSliderOptions.step !== 0
                "
                v-model="configuration.durationSliderValue"
                :min="configuration.durationSliderOptions.min"
                :max="configuration.durationSliderOptions.max"
                :step="configuration.durationSliderOptions.step"
                label="Time filter"
                color="rgb(51, 204, 255)"
                thumb-label
              >
                <template v-slot:thumb-label="item"
                  ><span style="color: black">{{ item.value }}</span></template
                >
              </v-range-slider>
            </v-col>
            <v-divider class="mb-0"></v-divider>
            <v-col class="px-3">
              <v-row no-gutters>
                <v-col cols="8">
                  <v-text-field
                    value="Display 0 on axis"
                    dense
                    solo
                    flat
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-checkbox
                    v-model="configuration.xzero"
                    id="inputxzero"
                    label="X"
                  ></v-checkbox>
                </v-col>
                <v-col cols="2">
                  <v-checkbox
                    v-model="configuration.yzero"
                    id="inputyzero"
                    label="Y"
                  ></v-checkbox>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="8">
                  <v-text-field
                    value="Logarithmic axis"
                    dense
                    solo
                    flat
                  ></v-text-field>
                </v-col>
                <v-col cols="2">
                  <v-checkbox
                    v-model="configuration.xlog"
                    id="inputxlog"
                    label="X"
                  ></v-checkbox>
                </v-col>
                <v-col cols="2">
                  <v-checkbox
                    v-model="configuration.ylog"
                    id="inputylog"
                    label="Y"
                  ></v-checkbox>
                </v-col>
              </v-row>
            </v-col>
            <exo-form-alert
              class="ml-2 mr-2"
              v-for="item in warningMessages"
              v-bind:key="item.id"
              type="error"
              >{{ item.message }}</exo-form-alert
            >
          </v-form>
        </v-card-text>
      </v-card>
      <v-row no-gutters>
        <v-col>
          <div>
            <v-divider class="m-0"></v-divider>
            <v-container>
              <v-btn
                type="submit"
                @click="updatePlotData(false)"
                id="submitBtn"
                variant="primary"
                class="lg"
                >Draw</v-btn
              >
              <v-btn
                type="button"
                @click="cancelPlot()"
                id="cancelPlotBtn"
                variant="primary"
                class="lg"
                >Cancel plot</v-btn
              >
            </v-container>
          </div>
        </v-col>
      </v-row>
    </v-navigation-drawer>
  </div>
</template>

<script>
// import mixins dependencies
import loadPlotComponents from '../../components/Plot/plotComponents.js'
import plotInput from '../../mixins/plotInput.js'
import plotLayout from '../../mixins/plotLayout.js'
import plotData from '../../mixins/plotData.js'

import dataTools from '../../tools/data.js'
import Plotly from 'plotly.js-dist'
import { mapGetters } from 'vuex'
import 'vue-grid-layout'

loadPlotComponents()

export default {
  name: 'numerical-comparision-plot-config',

  mixins: [plotLayout, plotInput, plotData],

  props: {
    otherResultsLog: {
      type: Object,
      default: null
    }
  },

  mounted() {
    this.$root.$on('current-results-updated', this.currentResultsUpdatedHandler)
    this.$root.$on('set-plot-config', this.setPlotConfigHandler)
  },

  beforeDestroy() {
    this.hidePlotingArea()
    this.$root.$off(
      'current-results-updated',
      this.currentResultsUpdatedHandler
    )
    this.$root.$off('set-plot-config', this.setPlotConfigHandler)
  },

  computed: {
    ...mapGetters(['currentMission', 'currentResultsLog']),

    backgroundStarStyle() {
      return (
        'background: #3BE287'
      )
    },

    isWarningMessages() {
      return this.warningMessages && this.warningMessages.length > 0
    }
  },

  data() {
    return {
      drawer: false,
      scatterModesLineChart: [
        { label: 'Lines + Markers', value: 'lines+markers' },
        { label: 'Lines', value: 'lines' },
        { label: 'Markers', value: 'markers' }
      ],
      scatterShapes: [
        { label: 'Linear', value: 'linear' },
        { label: 'Spline', value: 'spline' }
      ],
      plotData: [],
      acceptPathUpdates: true,
      plotingArea: null,
      filteredResults: [],
      otherFilteredResults: [],
      warningMessages: [],
      configuration: null,
      displayForm: true,
      fieldIndexes: undefined,
      type: 'SimulationResult'
    }
  },

  methods: {
    currentResultsUpdatedHandler() {
      if (
        this.currentMission != null &&
        this.currentMission.typeName === 'SimulationRequest'
      ) {
        this.updateCurrentMission()
      }
    },
    setPlotConfigHandler(config, plotingArea) {
      this.configuration = config
      this.plotingArea = plotingArea
      this.updateCurrentMission()
    },

    updateCurrentMission() {
      this.filteredResults = []
      this.otherFilteredResults = []
      this.colorIndex = 0
      this.plotData = []
      this.mission = this.currentMission

      if (
        this.mission != null &&
        this.currentResultsLog?.results?.ephemerides != null &&
        this.otherResultsLog?.results?.ephemerides != null
      ) {
        this.filteredResults = dataTools.convertSimulationData(
          this.currentResultsLog.results.ephemerides,
          this.currentResultsLog.results.fieldIndexes
        )
        this.otherFilteredResults = dataTools.convertSimulationData(
          this.otherResultsLog.results.ephemerides,
          this.otherResultsLog.results.fieldIndexes
        )
        this.fieldIndexes = this.currentResultsLog.results.fieldIndexes

        if (this.configuration?.x != null) {
          this.configuration.x.path = ['simulationDuration']
          this.configuration.x.value = 'simulationDuration'
          this.treeUpdated(this.configuration.x, 'x')
        }
        // set ref for slider
        const durationCurrent =
          this.currentResultsLog.results.maneuverReport?.simulationDuration
        const durationOther =
          this.currentResultsLog.results.maneuverReport?.simulationDuration
        if (durationCurrent > durationOther) {
          this.plotData = this.filteredResults
        } else {
          this.plotData = this.otherFilteredResults
        }
        this.displayForm = true
        this.hidePlotingArea()
      } else {
        this.hidePlotingArea()
      }
    },

    updateSliderData() {
      let sliderValues = []
      if (
        this.configuration.x &&
        this.configuration.x.path &&
        this.configuration.x.path[0] === 'simulationDuration' &&
        this.configuration.x.unit !== 'DHMS' &&
        this.configuration.x.unit !== '24h00'
      ) {
        sliderValues = this.getPlotValues(this.configuration.x, [
          ...this.filteredResults,
          ...this.otherFilteredResults
        ])
      }
      if (sliderValues.length === 0) {
        this.configuration.durationSliderOptions = {
          label: 'Error reading parameters',
          min: 0,
          max: 0,
          step: 0
        }
      } else {
        this.configuration.durationSliderOptions = {
          label: 'Time Interval',
          min: Math.min(...sliderValues).toFixed(2),
          max: Math.max(...sliderValues).toFixed(2)
        }

        // TODO better step formula
        this.configuration.durationSliderOptions.step = (
          (this.configuration.durationSliderOptions.max -
            this.configuration.durationSliderOptions.min) /
          sliderValues.length
        ).toFixed(2)
      }
      this.configuration.durationSliderValue = [
        this.configuration.durationSliderOptions.min,
        this.configuration.durationSliderOptions.max
      ]
    },

    hidePlotingArea() {
      if (document.getElementById(this.plotingAreaName)) {
        try {
          Plotly.purge(this.plotingAreaName)
        } catch (e) {}
        this.configuration.isDrawn = false
      }
    },

    cancelPlot() {
      this.drawer = false
    },

    pathUpdated(parameter) {
      let fieldType = dataTools.getFieldTypeFromPath(
        parameter.path,
        this.type,
        null
      )
      parameter.unitOptions = dataTools.getAvailableUnits(fieldType)
      parameter.unit = dataTools.getDefaultGuiUnit(fieldType)
      parameter.label = dataTools.getDataLabel(parameter.path)
    },

    pathFilterUpdated(parameter) {
      if (parameter.treevalue) {
        parameter.path = parameter.treevalue.split('.')
        let fieldType = dataTools.getFieldTypeFromPath(
          parameter.path,
          this.type,
          null
        )
        parameter.unitOptions = dataTools.getAvailableUnits(fieldType)
        parameter.unit =
          dataTools.getDefaultGuiUnit(fieldType) === 'DHMS'
            ? 'days'
            : dataTools.getDefaultGuiUnit(fieldType)
        parameter.valueOptions = dataTools
          .getUniqueValues(this.plotData, parameter.path)
          .map((val) => ({
            value: val,
            text: dataTools.convertFromSi(fieldType, val, parameter.unit)
          }))
        parameter.value = parameter.valueOptions
      }
    },

    // Draw clicked
    updatePlotData(fromLayoutSave) {
      let config = this.configuration == null ? {} : this.configuration
      this.colorIndex = 0
      config.data = []
      this.warningMessages = []
      config.warningMessages = []
      this.initLayout(config)
      this.updateSliderData()
      this.addTrace('Current', this.filteredResults)
      this.addTrace('Other', this.otherFilteredResults)
      config.warningMessages = this.warningMessages
      if (config.warningMessages.length === 0) {
        this.configuration.isDrawn = true
        this.drawer = false
        this.updatePlot(this.plotingArea, fromLayoutSave)
      }
    },

    checkUncompleteConfiguration(values, results) {},

    addTrace(traceName, results) {
      let config = this.configuration
      let values = this.initScatter(config)
      if (traceName !== '') {
        values.name = traceName
      }
      if (config?.y?.path?.length === 0) {
        return
      }
      values.x = this.getPlotValues(config.x, results)
      values.y = this.getPlotValues(config.y, results)
      // if not the other data inside = otherValues.y = [] =
      if (
        values.y.length === 0 ||
        !values.y.filter((el) => !isNaN(el)).length > 0
      ) {
        this.addWarning(
          'The mission to compare does not contain this type of ephemerides. You may choose another type or recompute the mission with different outputs.'
        )
      }
      this.setBasicLayoutY(config)
      this.setScatterValues(config, values)
      this.checkUncompleteConfiguration(values, results)
      config.data.push(values)
    },

    addWarning(message) {
      if (
        this.warningMessages.filter((el) => el.message === message).length === 0
      ) {
        this.warningMessages.push({
          message: message,
          id: this.warningMessages.length
        })
      }
    }
  }
}
</script>

<style scoped>
.d-grid-nav {
  height: 100%;
  display: inline-grid;
}
.card-bottom {
  display: inline-block;
  align-self: flex-end;
}
</style>
